import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { FirestoreContextService } from './firestore-context.service';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../environments/environment';
interface NotificationPayload {
  waiter?: {
    title: string;
    body: string;
    actionType: string;
    typeOfPayment?: number;
    regionId?: string;
    regionName?: string;
    tableIdentifier?: string;
    tableName?: string;
  };
  order?: {
    orderId: number;
    tableId: string;
    clientId: string;
    imageUrl: string;
    regionId?: string;
    regionName?: string;
    tableIdentifier?: string;
    tableName?: string;
    orderOrigin?: 'venue' | 'delivery' | 'pickup';
  };
}

interface RestaurantTable {
  id: string;
  businessId: number;
  name: string;
  numberOfSeats: number;
  assignedStaffId?: string;
  assignedStaff?: {
    fcmToken?: string;
    source?: number; // 1 = iOS, 2 = Android
  };
  regionId?: string;
  regionName?: string;
}

@Injectable({
  providedIn: 'root'
})

export class NotificationService {

  constructor(
    private http: HttpClient,
    private afs: AngularFirestore,
    private fsContext: FirestoreContextService,
    private translate: TranslateService
  ) { }

  private async getAccessToken(): Promise<string> {
    try {
      const headers = new HttpHeaders({
        Authorization: `Bearer 7xPqRtYmW3vZnLc8fBkJ9mPqRtYx3vZnWcL8fB2n`,
        'Content-Type': 'application/json'
      });

      const response: any = await this.http.post(
        environment.getAccessTokenUrl,
        {},
        { headers }
      ).toPromise();

      return response.accessToken;
    } catch (error) {
      console.error('Error fetching access token:', error);
      throw error;
    }
  }

  private async createRestaurantAction(tableNumber: number, actionType: string, typeOfPayment?: number): Promise<{ docId: string, regionId: string | null, regionName: string | null, tableIdentifier: string | null, tableName: string | null }> {
    const tableIdentifier = sessionStorage.getItem('tableIdentifier');
    const clientId = sessionStorage.getItem('clientId');
    let tableData: RestaurantTable | null = null;

    if (tableIdentifier) {
      const snapshot = await this.fsContext.run(() =>
        this.afs.collection('tables', ref => ref.where('id', '==', tableIdentifier)).get()
      ).toPromise();
      if (snapshot && !snapshot.empty) {
        tableData = snapshot.docs[0].data() as RestaurantTable;
      }
    }
    try {
      const actionData = {
        actionType: actionType === 'waiter' ? 1 : 2,
        status: 1,
        date: new Date(),
        shopId: parseInt(clientId!),
        tableNumber: tableNumber || null,
        token: sessionStorage.getItem('authToken'),
        origin: 3,
        typeOfPayment: typeOfPayment || null,
        orderId: null,
        totalAmount: null,
        numberOfProducts: null,
        tableIdentifier: tableData?.id || null,
        tableName: tableData?.name || null,
        regionId: tableData?.regionId || null,
        regionName: tableData?.regionName || null
      };

      const docRef = await this.fsContext.run(() => this.afs.collection('restaurant-actions')).add(actionData);
      // Add the document ID to the document
      await docRef.update({ id: docRef.id });
      return {
        docId: docRef.id,
        regionId: tableData?.regionId || null,
        regionName: tableData?.regionName || null,
        tableIdentifier: tableData?.id || null,
        tableName: tableData?.name || null
      };
    } catch (error: any) {
      console.error('Error creating restaurant action:', error);
      throw error;
    }
  }

  private async getTableFcmToken(tableId: string, isTableIdentifier: boolean): Promise<any> {
    try {
      const shopId = sessionStorage.getItem('clientId');
      if (!shopId) return null;
      let snapshot;

      if (isTableIdentifier) {
        snapshot = await this.fsContext.run(() =>
          this.afs.collection<RestaurantTable>('tables', ref =>
            ref.where('businessId', '==', parseInt(shopId))
              .where('id', '==', tableId)
          ).get()
        ).toPromise();
      } else {
        snapshot = await this.fsContext.run(() =>
          this.afs.collection<RestaurantTable>('tables', ref =>
            ref.where('businessId', '==', parseInt(shopId))
              .where('name', '==', tableId)
          ).get()
        ).toPromise();
      }

      if (!snapshot || snapshot.empty) return null;

      const tableDoc = snapshot.docs[0];
      const tableData = tableDoc.data() as RestaurantTable;

      // If table has assigned staff with FCM token and source, return it
      if (tableData.assignedStaff?.fcmToken && tableData.assignedStaff?.source) {
        return [{ fcmToken: tableData.assignedStaff.fcmToken, source: tableData.assignedStaff.source }];
      }

      // Otherwise, check the region for assigned users with FCM tokens and source
      if (tableData.regionId) {
        const regionSnap = await this.fsContext.run(() =>
          this.afs.collection('regions').doc(tableData.regionId).get()
        ).toPromise();
        if (regionSnap && regionSnap.exists) {
          const regionData = regionSnap.data() as any;
          if (regionData && Array.isArray(regionData.assignedStaff)) {
            // Collect all FCM tokens and sources from assigned users
            const tokens = regionData.assignedStaff
              .filter((user: any) => user.fcmToken && user.source)
              .map((user: any) => ({ fcmToken: user.fcmToken, source: user.source }));
            if (tokens.length > 0) {
              return tokens;
            }
          }
        }
      }
      return null;
    } catch (error) {
      console.error('Error fetching table FCM token:', error);
      return null;
    }
  }

  private async getOrderNotificationTitle(orderOrigin?: 'venue' | 'delivery' | 'pickup'): Promise<string> {
    let key: string;
    switch (orderOrigin) {
      case 'venue':
        key = 'new_order_notification_title_venue';
        break;
      case 'delivery':
        key = 'new_order_notification_title_delivery';
        break;
      case 'pickup':
        key = 'new_order_notification_title_pickup';
        break;
      default:
        key = 'new_order_notification_title';
    }
    const translation = await this.translate.get(key).toPromise();
    return translation || 'New Order';
  }

  private async getOrderNotificationBody(orderId: number, orderOrigin?: 'venue' | 'delivery' | 'pickup', tableName?: string): Promise<string> {
    let key: string;
    switch (orderOrigin) {
      case 'venue':
        key = 'new_order_notification_description_venue';
        break;
      case 'delivery':
        key = 'new_order_notification_description_delivery';
        break;
      case 'pickup':
        key = 'new_order_notification_description_pickup';
        break;
      default:
        key = 'new_order_notification_description';
    }
    const translation = await this.translate.get(key).toPromise();
    if (!translation) {
      return `New order with id: ${orderId} is created.`;
    }
    // Replace %@ placeholders
    let body = translation.replace('%@', orderId.toString());
    if (orderOrigin === 'venue' && tableName) {
      // For venue orders, there's a second %@ for table name
      body = body.replace('%@', tableName);
    }
    return body;
  }

  private async createAndroidPayload(payload: NotificationPayload, fcmToken?: string | null) {
    if (payload.waiter) {
      const notificationType = payload.waiter.typeOfPayment
        ? (payload.waiter.typeOfPayment === 1 ? 'call_receipt_cash' : 'call_receipt_card')
        : 'call_waiter';

      return {
        message: {
          ...(fcmToken ? { token: fcmToken } : { topic: `users_on_shift_android_${sessionStorage.getItem('clientId')}` }),
          data: {
            title: payload.waiter.title,
            body: payload.waiter.body,
            action_id: payload.waiter.actionType.toString(),
            table_id: sessionStorage.getItem('tableId') || payload.waiter.tableName,
            notification_type: fcmToken ? notificationType : notificationType + '_shift',
            regionId: payload.waiter.regionId || '',
            regionName: payload.waiter.regionName || '',
          }
        }
      };
    } else if (payload.order) {
      const orderOrigin = payload.order.orderOrigin || 'venue';
      const [title, body] = await Promise.all([
        this.getOrderNotificationTitle(orderOrigin),
        this.getOrderNotificationBody(payload.order.orderId, orderOrigin, payload.order.tableName)
      ]);
      
      const dataPayload: any = {
        body: body,
        order_id: payload.order.orderId.toString(),
        title: title,
        notification_type: fcmToken ? 'new_order' : 'new_order_shift',
        order_origin: orderOrigin,
        regionId: payload.order.regionId || '',
        regionName: payload.order.regionName || '',
        tableIdentifier: payload.order.tableId || '',
        tableName: payload.order.tableName || ''
      };

      // Only include table_id for venue orders
      if (orderOrigin === 'venue') {
        dataPayload.table_id = sessionStorage.getItem('tableId') || payload.order.tableName;
      }

      return {
        message: {
          ...(fcmToken ? { token: fcmToken } : { topic: `users_on_shift_android_${sessionStorage.getItem('clientId')}` }),
          data: dataPayload
        }
      };
    }
    throw new Error('Invalid payload type');
  }

  private async createIosPayload(payload: NotificationPayload, fcmToken?: string | null) {

    const clientData = JSON.parse(sessionStorage.getItem('clientData') || '{}');

    if (payload.waiter) {
      const notificationType = payload.waiter.typeOfPayment
        ? (payload.waiter.typeOfPayment === 1 ? 'call_receipt_cash' : 'call_receipt_card')
        : 'call_waiter';

      return {
        message: {
          ...(fcmToken ? { token: fcmToken } : { topic: `users_on_shift_ios_${sessionStorage.getItem('clientId')}` }),
          notification: {
            title: payload.waiter.title,
            body: payload.waiter.body,
          },
          apns: {
            payload: {
              aps: {
                'mutable-content': 1,
                image_url: clientData.ios_header_url,
                sound: "default"
              }
            }
          },
          data: {
            action_id: payload.waiter.actionType.toString(),
            image: clientData.image,
            notification_type: fcmToken ? notificationType : notificationType + '_shift',
            table_id: sessionStorage.getItem('tableId'),
            regionId: payload.waiter.regionId || '',
            regionName: payload.waiter.regionName || '',
            tableIdentifier: payload.waiter.tableIdentifier || '',
            tableName: payload.waiter.tableName || ''
          }
        }
      };
    } else if (payload.order) {
      const orderOrigin = payload.order.orderOrigin || 'venue';
      const [title, body] = await Promise.all([
        this.getOrderNotificationTitle(orderOrigin),
        this.getOrderNotificationBody(payload.order.orderId, orderOrigin, payload.order.tableName)
      ]);
      
      const dataPayload: any = {
        order_id: payload.order.orderId.toString(),
        image: payload.order.imageUrl,
        notification_type: fcmToken ? 'new_order' : 'new_order_shift',
        order_origin: orderOrigin,
        regionId: payload.order.regionId || '',
        regionName: payload.order.regionName || '',
        tableIdentifier: payload.order.tableId || '',
        tableName: payload.order.tableName || ''
      };

      // Only include table_id for venue orders
      if (orderOrigin === 'venue') {
        dataPayload.table_id = payload.order.tableId;
      }

      return {
        message: {
          ...(fcmToken ? { token: fcmToken } : { topic: `users_on_shift_ios_${sessionStorage.getItem('clientId')}` }),
          notification: {
            title: title,
            body: body
          },
          apns: {
            payload: {
              aps: {
                'mutable-content': 1,
                image_url: payload.order.imageUrl,
                sound: 'default'
              }
            }
          },
          data: dataPayload
        }
      };
    }
    throw new Error('Invalid payload type');
  }

  async sendNotification(payload: NotificationPayload): Promise<void> {
    try {
      const headers = new HttpHeaders({
        Authorization: `Bearer ${await this.getAccessToken()}`,
        'Content-Type': 'application/json'
      });

      let fcmToken: any = null;
      // Get tableId from sessionStorage
      const tableId = sessionStorage.getItem('tableId');
      const tableIdentifier = sessionStorage.getItem('tableIdentifier')
      if (payload.waiter) {
        if (tableId) {
          fcmToken = await this.getTableFcmToken(tableId, false);
        } else if (tableIdentifier) {
          fcmToken = await this.getTableFcmToken(tableIdentifier, true)
        }
      } else if (payload.order) {
        // For delivery/pickup orders, tableId will be empty, so skip table lookup
        // and use topic-based notifications instead
        if (payload.order.tableId && payload.order.tableId.trim() !== '') {
          let isTableIdentifier = tableIdentifier ? true : false;
          fcmToken = await this.getTableFcmToken(payload.order.tableId, isTableIdentifier);
        }
        // If tableId is empty (delivery/pickup), fcmToken remains null and will use topic-based notifications
      }

      // If we have assignedStaff(s) with source, send only to the correct platform
      if (Array.isArray(fcmToken) && fcmToken.length > 0 && fcmToken[0].source) {
        const notificationPromises: Promise<any>[] = [];
        for (const staff of fcmToken) {
          if (staff.source === 1) {
            // iOS only
            const iosPayload = await this.createIosPayload(payload, staff.fcmToken);
            notificationPromises.push(this.http.post(environment.fcmUrl, iosPayload, { headers }).toPromise());
          } else if (staff.source === 2) {
            // Android only
            const androidPayload = await this.createAndroidPayload(payload, staff.fcmToken);
            notificationPromises.push(this.http.post(environment.fcmUrl, androidPayload, { headers }).toPromise());
          }
        }
        await Promise.all(notificationPromises);
      } else if (Array.isArray(fcmToken)) {
        // Old logic: send to all tokens on both platforms
        const notificationPromises: Promise<any>[] = [];
        for (const token of fcmToken) {
          const androidPayload = await this.createAndroidPayload(payload, token);
          const iosPayload = await this.createIosPayload(payload, token);
          notificationPromises.push(
            this.http.post(environment.fcmUrl, androidPayload, { headers }).toPromise(),
            this.http.post(environment.fcmUrl, iosPayload, { headers }).toPromise()
          );
        }
        await Promise.all(notificationPromises);
      } else {
        // Single token, old logic
        const [androidPayload, iosPayload] = await Promise.all([
          this.createAndroidPayload(payload, fcmToken),
          this.createIosPayload(payload, fcmToken)
        ]);
        const notificationPromises = [
          this.http.post(environment.fcmUrl, androidPayload, { headers }).toPromise(),
          this.http.post(environment.fcmUrl, iosPayload, { headers }).toPromise()
        ];
        await Promise.all(notificationPromises);
      }
    } catch (error) {
      console.error('Error sending notifications:', error);
      throw error;
    }
  }

  async sendWaiterNotification(tableId: string, type: 'waiter' | 'receipt-cash' | 'receipt-card'): Promise<void> {
    const restorauntAction = await this.createRestaurantAction(
      parseInt(tableId),
      type,
      type === 'receipt-cash' ? 1 : type === 'receipt-card' ? 2 : undefined
    );

    const tableName = restorauntAction.tableName === null ? tableId : restorauntAction.tableName

    const title = type === 'waiter' ? 'Call Waiter' : 'Receipt Request';
    const body = `Table ${tableName} has requested a ${type === 'waiter' ? 'waiter' : 'receipt'}`;

    await this.sendNotification({
      waiter: {
        title,
        body,
        actionType: restorauntAction.docId,
        typeOfPayment: type === 'receipt-cash' ? 1 : type === 'receipt-card' ? 2 : undefined,
        regionId: restorauntAction.regionId || '',
        regionName: restorauntAction.regionName || '',
        tableIdentifier: restorauntAction.tableIdentifier || '',
        tableName: restorauntAction.tableName || ''
      }
    });
  }

  async sendOrderNotification(orderId: number, tableId: string, clientId: string, imageUrl: string, regionName: string, regionId: string, tableName: string, orderOrigin: 'venue' | 'delivery' | 'pickup' = 'venue'): Promise<void> {
    await this.sendNotification({
      order: {
        orderId,
        tableId,
        clientId,
        imageUrl,
        regionName,
        regionId,
        tableName,
        orderOrigin,
      }
    });
  }
}

